import React, { useState, useMemo } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

/**
 * A reusable, sortable Table component for the application.
 *
 * @param {Array} data - Array of objects to render as rows.
 * @param {Array} columns - Array of column configurations:
 *   - header: string | ReactNode (Column title)
 *   - accessor: string (Key in the data object)
 *   - sortAccessor: string | function (Optional: Key or function to use for sorting instead of accessor)
 *   - sortable: boolean (Optional: Defaults to true. If false, disables sorting for this column)
 *   - render: function (Optional: (row, index) => ReactNode to custom render the cell content)
 *   - align: 'left' | 'center' | 'right' (Optional: Text alignment for the column data, defaults to 'left')
 *   - className: string (Optional: Additional classes for the header/cells of this column)
 * @param {Number} defaultSortColumn - Index of the column to sort by default (Defaults to 0).
 * @param {Function} keyExtractor - Optional function to extract a unique key for each row.
 * @param {String} emptyMessage - Optional message to show when data is empty.
 * @param {String} containerClassName - Optional classes for the table container.
 */
export default function Table({
  data = [],
  columns = [],
  defaultSortColumn = 0,
  keyExtractor,
  emptyMessage,
  containerClassName = "bg-gray-800 rounded-2xl shadow overflow-hidden",
  tableClassName = "min-w-full text-sm",
  headerClassName = "bg-gray-700 sticky top-0 z-10 text-gray-300",
  rowClassName = "border-b border-gray-700 hover:bg-gray-700/30 transition-colors"
}) {
  const { t } = useTranslation();

  // Initialize sort config with the default column (if it is sortable)
  const initialSortConfig = useMemo(() => {
    if (columns.length > 0 && columns[defaultSortColumn] && columns[defaultSortColumn].sortable !== false) {
      return {
        key: columns[defaultSortColumn].accessor,
        sortAccessor: columns[defaultSortColumn].sortAccessor,
        direction: 'asc',
      };
    }
    return null;
  }, [columns, defaultSortColumn]);

  const [sortConfig, setSortConfig] = useState(initialSortConfig);

  // Sorting handler
  const requestSort = (column) => {
    if (column.sortable === false) return;

    const sortKey = column.accessor;
    let direction = 'asc';

    if (sortConfig && sortConfig.key === sortKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({
      key: sortKey,
      sortAccessor: column.sortAccessor,
      direction,
    });
  };

  // Helper to get nested object values by string path (e.g. "user.firstName")
  const getNestedValue = (obj, path) => {
    if (!path) return undefined;
    if (typeof path === 'function') return path(obj);
    return path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
  };

  // Sort data
  const sortedData = useMemo(() => {
    const sortableItems = [...data];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue = getNestedValue(a, sortConfig.sortAccessor || sortConfig.key);
        let bValue = getNestedValue(b, sortConfig.sortAccessor || sortConfig.key);

        // Handle string comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        // Handle null/undefined (push to bottom)
        if (aValue === undefined || aValue === null) return 1;
        if (bValue === undefined || bValue === null) return -1;

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  // Handle empty state
  if (!data || data.length === 0) {
    return (
      <div className={containerClassName}>
        <div className="p-12 text-center text-gray-500">
          {emptyMessage || t('noDataFound') || 'No data available'}
        </div>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <div className="max-h-[70vh] overflow-auto">
        <table className={tableClassName}>
          <thead className={headerClassName}>
            <tr>
              {columns.map((col, index) => {
                const isSortable = col.sortable !== false;
                const isSorted = sortConfig && sortConfig.key === col.accessor;

                let alignClass = 'text-left';
                if (col.align === 'center') alignClass = 'text-center';
                if (col.align === 'right') alignClass = 'text-right';

                return (
                  <th
                    key={col.accessor || index}
                    onClick={() => requestSort(col)}
                    className={`px-4 sm:px-6 py-3.5 ${alignClass} ${isSortable ? 'cursor-pointer select-none hover:bg-gray-600 transition-colors' : ''} ${col.className || ''}`}
                  >
                    <div className={`flex items-center gap-1.5 ${col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : 'justify-start'}`}>
                      <span>{col.header}</span>
                      {isSortable && (
                        <div className="flex flex-col opacity-50">
                          <ChevronUpIcon className={`w-2.5 h-2.5 ${(isSorted && sortConfig.direction === 'asc') ? 'text-blue-400 opacity-100 font-bold' : ''}`} />
                          <ChevronDownIcon className={`w-2.5 h-2.5 -mt-1 ${(isSorted && sortConfig.direction === 'desc') ? 'text-blue-400 opacity-100 font-bold' : ''}`} />
                        </div>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, rowIndex) => {
              const rowKey = keyExtractor ? keyExtractor(row, rowIndex) : (row._id || row.id || rowIndex);
              return (
                <tr key={rowKey} className={rowClassName}>
                  {columns.map((col, colIndex) => {
                    let alignClass = 'text-left';
                    if (col.align === 'center') alignClass = 'text-center';
                    if (col.align === 'right') alignClass = 'text-right';

                    const cellValue = getNestedValue(row, col.accessor);

                    return (
                      <td key={`${rowKey}-${col.accessor || colIndex}`} className={`px-4 sm:px-6 py-4 ${alignClass} ${col.className || ''}`}>
                        {col.render ? col.render(row, rowIndex) : (cellValue !== undefined && cellValue !== null ? cellValue : '—')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
